"use client";
import useResponsiveSize from "@/hooks/useResponsiveSize";
import { Input, InputProps, Spin } from "antd";
import { useState } from "react";
import { IoSend } from "react-icons/io5";

interface InputWithSubmitIconProps {
  id?: string;
  label?: string;
  name?: string;
  value?: string;
  disabled?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  size?: "small" | "middle" | "large";
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  type?: "text" | "email" | "number" | "tel" | "url" | "date" | "file";
  errorMessage?: string;
  touched?: boolean;
  // Submit icon props
  showSubmitIcon?: boolean;
  submitIcon?: React.ReactNode;
  onSubmitClick?: () => void;
  loading?: boolean;
  [key: string]: any;
}

const InputWithSubmitIcon: React.FC<
  InputWithSubmitIconProps & Omit<InputProps, "size" | "onChange" | "onBlur">
> = ({
  id,
  label,
  name,
  value,
  disabled,
  onChange,
  onBlur,
  size,
  placeholder,
  required,
  error,
  type = "text",
  errorMessage,
  touched,
  showSubmitIcon = true,
  submitIcon,
  onSubmitClick,
  loading = false,
  ...rest
}) => {
  const [focused, setFocused] = useState(false);
  const responsiveSize = useResponsiveSize();

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(value !== "");
    if (onBlur) onBlur(event);
  };

  const status = error ? "error" : undefined;

  // Default submit icon
  const defaultSubmitIcon = submitIcon || (
    <IoSend className="text-gray-600 hover:text-gray-700 transition-colors " />
  );

  // Handle submit click
  const handleSubmitClick = () => {
    if (onSubmitClick && !disabled && !loading) {
      onSubmitClick();
    }
  };

  // Render submit icon or spinner
  const renderSubmitIcon = () => {
    if (loading) {
      return (
        <Spin size="small" className="flex items-center justify-center">
          <IoSend className="text-gray-400" />
        </Spin>
      );
    }
    return defaultSubmitIcon;
  };

  return (
    <div>
      {label && (
        <label
          className={`${
            disabled ? "text-[#5B6B79]" : "text-text-primary"
          } font-medium text-xs md:text-sm`}
        >
          {label}
        </label>
      )}
      <div className="mt-1">
        <Input
          id={id}
          type={type}
          name={name}
          value={value}
          size={size || responsiveSize}
          placeholder={placeholder}
          autoComplete="off"
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          disabled={disabled}
          status={status}
          suffix={
            showSubmitIcon ? (
              <button
                type="button"
                onClick={handleSubmitClick}
                className="flex items-center justify-center p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                disabled={disabled || loading}
              >
                {renderSubmitIcon()}
              </button>
            ) : undefined
          }
          {...rest}
          data-touched={touched ? "true" : "false"}
        />
      </div>

      <div
        className={`text-[10px] text-[#FF1F1F] mt-1 absolute transition-all duration-300 ease-in-out 
        ${
          error
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-95 -translate-y-1"
        }`}
      >
        {errorMessage}
      </div>
    </div>
  );
};

export default InputWithSubmitIcon;
